import React, { useState, useEffect, useCallback, useRef } from 'react';
import { updateCreditConfiguration } from 'apis/adminServices/creditServices';
import { useToastStore } from 'store/toastStore';

interface CreditConfig {
    id: number;
    name: string;
    date: string;
    status: 'Active' | 'Disabled';
    answer: number;
    request: number;
    invite: number;
    signup: number;
    initialFive: number;
}

interface EditModalProps {
    isOpen: boolean;
    credit: CreditConfig;
    onClose: () => void;
    onUpdate?: (updatedCredit: CreditConfig) => void;
}

const EditModal: React.FC<EditModalProps> = ({ isOpen, credit, onClose, onUpdate }) => {
    const [formData, setFormData] = useState<CreditConfig>(credit);
    const [isLoading, setIsLoading] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToastStore();
    const [creditErrors, setCreditErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isOpen) {
            console.log('Initializing form data with credit:', credit);
            setFormData(credit);
            setIsLoading(false);
            nameInputRef.current?.focus();
        }
    }, [isOpen, credit]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isLoading) onClose();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose, isLoading]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        if (name !== 'name' && name !== 'status') {
            const numValue = parseInt(value || '0', 10);
            if (numValue > 100) {
                setCreditErrors(prev => ({...prev, [name]: 'Credit value cannot exceed 100'}));
                return;
            }
            setCreditErrors(prev => ({...prev, [name]: ''}));
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: name === 'name' ? value :
                    name === 'status' ? value as 'Active' | 'Disabled' :
                    Math.max(0, parseInt(value || '0', 10)),
        }));
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        const apiPayload = {
            configurationName: formData.name,
            answer: formData.answer,
            request: formData.request,
            invite: formData.invite,
            signup: formData.signup,
            initialFive: formData.initialFive,
            isActive: formData.status === 'Active',
            isPromotion: false,
            validFrom: new Date().toISOString().split('T')[0],
            validTo: '2099-12-31',
            description: '',
        };

        try {
            const response = await updateCreditConfiguration(formData.id, apiPayload);
            const responseMessage = typeof response === 'string' ? response : 'Configuration updated successfully';

            const updatedCredit: CreditConfig = { ...formData };
            onUpdate?.(updatedCredit);

            showToast(responseMessage,
                'success'
            );
            onClose();
        } catch (err) {
            // err is now the full AxiosResponse object
            const errorResponse = err as { data: string | { detail?: string; title?: string; message?: string }; status: number };
            let errorMessage: string;

            if (typeof errorResponse.data === 'string') {
                errorMessage = errorResponse.data;
            } else if (errorResponse.data && typeof errorResponse.data === 'object') {
                errorMessage = errorResponse.data.detail || 
                              errorResponse.data.title || 
                              errorResponse.data.message || 
                              `Error ${errorResponse.status}: Failed to update configuration`;
            } else {
                errorMessage = 'Failed to update configuration';
            }

            showToast(errorMessage,
               'error'
            );
            console.error('Update error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [formData, onClose, onUpdate, showToast]);

    const inputFields = [
        { name: 'name', label: 'Configuration Name', type: 'text', required: true },
        { name: 'request', label: 'Tokens for Posting a Load', type: 'number', required: false },
        { name: 'answer', label: 'Tokens for Answering a Request', type: 'number', required: false },
        // { name: 'signup', label: 'Credits for Initial Signup', type: 'number' },
        { name: 'initialFive', label: 'Tokens for the First 5 Answers', type: 'number', required: false },
        { name: 'invite', label: 'Tokens for Referrals', type: 'number', required: false },
    ];

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={onClose}
        >
            <div 
                className="relative p-4 w-full max-w-xl"
                onClick={e => e.stopPropagation()}
            >
                <div 
                    className="relative bg-white rounded-lg shadow pb-4"
                    role="dialog"
                    aria-labelledby="modal-title"
                >
                    <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
                        <p id="modal-title" className="text-base font-semibold text-grease-100">
                            Edit Configuration
                        </p>
                        <button
                            type="button"
                            className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center disabled:opacity-50"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                                />
                            </svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="px-4 py-4">
                        {inputFields.map(({ name, label, type, required }) => (
                            <div key={name} className="mt-2">
                                <label 
                                    htmlFor={name} 
                                    className="text-xs font-light text-gray-500"
                                >
                                    {label}
                                    {required ? <span className="text-red-500 ml-1">*</span> : ""}
                                </label>
                                <input
                                    id={name}
                                    type={type}
                                    name={name}
                                    value={formData[name as keyof CreditConfig]}
                                    onChange={handleInputChange}
                                    className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 disabled:bg-gray-200 border-none"
                                    disabled={isLoading}
                                    required
                                    min={type === 'number' ? 0 : undefined}
                                    max={type === 'number' ? 100 : undefined}
                                    ref={name === 'name' ? nameInputRef : null}
                                />
                                {creditErrors[name] && <p className="text-red-500 text-xs mt-1">{creditErrors[name]}</p>}
                            </div>
                        ))}

                        <div className="mt-2">
                            <label 
                                htmlFor="status" 
                                className="text-xs font-light text-gray-500"
                            >
                                Status
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5 mt-2 disabled:bg-gray-200 border-none"
                                disabled={isLoading}
                                required
                            >
                                <option value="Active">Active</option>
                                <option value="Disabled">Disabled</option>
                            </select>
                        </div>

                        <div className="flex gap-4 items-center justify-end px-4 mt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex items-center gap-2 py-3 px-6 bg-gray-300 bg-opacity-20 rounded-md disabled:opacity-50"
                                disabled={isLoading}
                            >
                                <p className="font-medium text-gray-500 text-xs">Cancel</p>
                            </button>
                            <button
                                type="submit"
                                className="flex items-center gap-2 py-3 px-6 bg-[#FFD700] rounded-md disabled:bg-[#FFD700] disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                <p className="font-medium text-white text-xs">
                                    {isLoading ? 'Saving...' : 'Save'}
                                </p>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditModal;